import { Injectable } from '@angular/core';
import { UpdateFormValue } from '@ngxs/form-plugin';

import { Action, State, StateContext } from '@ngxs/store';
import { catchError, tap } from 'rxjs/operators';

import { AvailabilityService, CourtBookingService, PersonService } from '@bayclubs/court-booking-employee-api';
import { BcStateForm, BcStateListContext } from '@bayclubs/state';
import { TimeDisplayPipe, TimeService } from '@bayclubs/ui-core';

import { CoreFacade } from '@core/core.module';

import { CourtBookingSaveFormModel, CourtBookingSaveTimeSlotModel } from '../models/court-booking-save-form.model';
import { CourtBookingSaveMapperService } from '../services/court-booking-save-mapper.service';
import * as actions from './court-booking-save.actions';
import { COURT_BOOKING_SAVE_FEATURE_KEY, CourtBookingSaveStateModel } from './court-booking-save.state.model';

const initialState = {
    players: BcStateListContext.Empty(),
    courtBookingForm: BcStateForm.Empty(),
    courts: BcStateListContext.Empty(),
    timeSlots: BcStateListContext.Empty(),
    playerRules: BcStateListContext.Empty()
} as CourtBookingSaveStateModel;

@Injectable({ providedIn: 'root' })
@State({ name: COURT_BOOKING_SAVE_FEATURE_KEY, defaults: initialState })
export class CourtBookingSaveState {
    constructor(
        private personApiService: PersonService,
        private courtBookingApiService: CourtBookingService,
        private coreFacade: CoreFacade,
        private timeService: TimeService,
        private availabilityApiService: AvailabilityService,
        private timeDisplayPipe: TimeDisplayPipe,
        private courtBookingMapper: CourtBookingSaveMapperService
    ) { }

    @Action(actions.SearchPlayersAction)
    searchPlayers(ctx: StateContext<CourtBookingSaveStateModel>, action: actions.SearchPlayersAction) {
        ctx.patchState({ players: BcStateListContext.Loading() });
        if (action.search) {
            return this.personApiService.api10PersonSearchGet({ searchPhrase: action.search, take: 30 }).pipe(
                tap(value => {
                    ctx.patchState({ players: BcStateListContext.Loaded(value.persons) });
                }),
                catchError(err => {
                    ctx.patchState({ players: BcStateListContext.Error() });
                    throw err;
                })
            );
        } else {
            return ctx.patchState({ players: BcStateListContext.Empty() });
        }
    }

    @Action(actions.LoadCourtsAction)
    loadCourts(ctx: StateContext<CourtBookingSaveStateModel>, action: actions.LoadCourtsAction) {
        const club = this.coreFacade.selectedClub();
        const date = action.date ? action.date.value : this.timeService.toBcDate(this.timeService.nowUtc()).value;
        ctx.patchState({ courts: BcStateListContext.Loading() });
        return this.courtBookingApiService.api10ClubIdCourtbookingAddBookingCourtsGet({ clubId: club.id, date }).pipe(
            tap(value => {
                ctx.patchState({ courts: BcStateListContext.Loaded(value.items) });
            }),
            catchError(err => {
                ctx.patchState({ courts: BcStateListContext.Error() });
                throw err;
            })
        );
    }

    @Action(actions.ClearSaveFormAction)
    clearSaveForm(ctx: StateContext<CourtBookingSaveStateModel>) {
        ctx.patchState({
            players: BcStateListContext.Empty(),
            courtBookingForm: BcStateForm.Empty(),
            courts: BcStateListContext.Empty(),
            playerRules: BcStateListContext.Empty(),
            timeSlots: BcStateListContext.Empty()
        });
    }

    @Action(actions.LoadAvailableTimeSlotsAction)
    loadAvailableTimeSlots(ctx: StateContext<CourtBookingSaveStateModel>, action: actions.LoadAvailableTimeSlotsAction) {
        const state = ctx.getState();
        const timeSlot = action.timeSlotId !== null ? action.timeSlotId : state.courtBookingForm.model.timeSlot;
        const date = action.date !== null ? action.date : state.courtBookingForm.model.date;
        if (timeSlot) {
            const court = state.courts.items.find(c => c.courtId === state.courtBookingForm.model.courtId);
            const categoryOption = court.courtOptions.find(
                co => co.categoryOptionsId === state.courtBookingForm.model.categoryOptionsId
            );
            ctx.patchState({ timeSlots: BcStateListContext.Loading() });
            return this.availabilityApiService
                .api10ClubIdAvailabilityGet({
                    clubId: state.courtBookingForm.model.clubId,
                    courtId: [court.courtId],
                    categoryOptionsId: state.courtBookingForm.model.categoryOptionsId,
                    categoryCode: categoryOption.category.code,
                    date: date.value,
                    personId: state.courtBookingForm.model.firstPlayer.personId,
                    timeSlotId: timeSlot
                })
                .pipe(
                    tap(value => {
                        const timeSlots = value.clubsAvailabilities[0].availableTimeSlots.map(
                            ts =>
                            ({
                                timeFromInMinutes: ts.fromInMinutes,
                                timeToInMinutes: ts.toInMinutes,
                                displayName: `${this.timeDisplayPipe.transform(
                                    ts.fromInMinutes
                                )} - ${this.timeDisplayPipe.transform(ts.toInMinutes)}`,
                                isUnavailable: !!ts.unavailability,
                                unavailabilityMessage: ts.unavailability?.message
                            } as CourtBookingSaveTimeSlotModel)
                        );
                        ctx.patchState({ timeSlots: BcStateListContext.Loaded(timeSlots) });
                        if (timeSlots.some(ts => ts.timeFromInMinutes === action.timeFromInMinutes)) {
                            const currentForm: CourtBookingSaveFormModel = ctx.getState().courtBookingForm.model;
                            const updatedModel: CourtBookingSaveFormModel = {
                                ...currentForm,
                                timeFromInMinutes: action.timeFromInMinutes
                            };

                            ctx.dispatch(
                                new UpdateFormValue({
                                    path: 'cbCourtBookingSave.courtBookingForm',
                                    value: updatedModel
                                })
                            );
                        }
                    }),
                    catchError(err => {
                        ctx.patchState({ timeSlots: BcStateListContext.Error() });
                        throw err;
                    })
                );
        } else {
            ctx.patchState({ timeSlots: BcStateListContext.Empty() });
        }
    }

    @Action(actions.ClearPlayersAction)
    clearPlayers(ctx: StateContext<CourtBookingSaveStateModel>) {
        ctx.patchState({ players: BcStateListContext.Empty() });
    }

    @Action(actions.SaveCourtBookingAction)
    saveCourtBooking(ctx: StateContext<CourtBookingSaveStateModel>, action: actions.SaveCourtBookingAction) {
        const state = ctx.getState();
        const club = this.coreFacade.selectedClub();
        const court = state.courts.items.find(c => c.courtId === state.courtBookingForm.model.courtId);
        const categoryOption = court.courtOptions.find(
            co => co.categoryOptionsId === state.courtBookingForm.model.categoryOptionsId
        );
        const timeSlot = categoryOption.timeSlots.find(ts => ts.courtTimeSlotId === state.courtBookingForm.model.timeSlot);
        const request = this.courtBookingMapper.createAddCourtBookingRequest(state.courtBookingForm.model, timeSlot);
        ctx.patchState({ courtBookingForm: BcStateForm.Loading(state.courtBookingForm, true) });
        return this.courtBookingApiService
            .api10ClubIdCourtbookingPost({ clubId: club.id, createCourtBookingRequest: request })
            .pipe(
                tap(value => {
                    ctx.dispatch(new actions.SaveCourtBookingSuccessAction());
                    ctx.patchState({ courtBookingForm: BcStateForm.Empty() });
                }),
                catchError(err => {
                    ctx.patchState({ courtBookingForm: BcStateForm.Loading(state.courtBookingForm, false) });
                    throw err;
                })
            );
    }

    @Action(actions.CheckPlayersMatrixAction)
    checkPlayersMatrix(ctx: StateContext<CourtBookingSaveStateModel>, action: actions.SaveCourtBookingAction) {
        const state = ctx.getState();
        if (
            state.courtBookingForm.model.courtId !== null &&
            state.courtBookingForm.model.date != null &&
            state.courtBookingForm.model.firstPlayer != null
        ) {
            const court = state.courts.items.find(c => c.courtId === state.courtBookingForm.model.courtId);
            const club = this.coreFacade.selectedClub();
            const request = this.courtBookingMapper.createCheckPersonsRulesMatrixRequest(
                state.courtBookingForm.model,
                club.id,
                court.category.code
            );
            ctx.patchState({ playerRules: BcStateListContext.Loading() });
            return this.personApiService.api10PersonCheckRulesMatrixPost({ checkPersonsRulesMatrixRequest: request }).pipe(
                tap(value => {
                    ctx.patchState({ playerRules: BcStateListContext.Loaded(value.items) });
                }),
                catchError(err => {
                    ctx.patchState({ playerRules: BcStateListContext.Error() });
                    throw err;
                })
            );
        }
    }
}
